import React, { useEffect, useState } from "react";
import { EntityLink } from "@/components/EntityLink";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { HandCoins, Wallet, CheckCircle2, Loader2 } from "lucide-react";
import { Stat, StatStrip } from "@/components/StatTile";

export default function AdminReferralPayouts() {
  const [data, setData] = useState({ rows: [], total_pending: 0, total_paid: 0 });
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [settling, setSettling] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/referral/payouts").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const settle = async () => {
    if (!target) return;
    setSettling(true);
    try {
      const { data: res } = await api.post(`/admin/referral/payouts/${target.referrer_id}/settle`);
      toast.success(`Liquidati ${euro(res.amount)} (${res.count} provvigioni)`);
      setTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore liquidazione");
    } finally { setSettling(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-referral-payouts-page">
      <div>
        <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Amministrazione</p>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-1">Payout Referral</h1>
        <p className="text-sm text-muted-foreground mt-2">Liquida le provvigioni "da incassare" dei dealer. Il pagamento è gestito fuori piattaforma: segniamo l'importo come "già pagato" con data.</p>
      </div>

      <StatStrip testid="referral-payout-stats">
        <Stat icon={HandCoins} label="Totale da incassare" value={euro(data.total_pending)} testid="referral-payout-pending" />
        <Stat icon={Wallet} label="Totale già pagato" value={euro(data.total_paid)} testid="referral-payout-paid" />
      </StatStrip>

      <Card className="glow-border">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Dealer / Agenzia</TableHead>
            <TableHead>Iscritti attivi</TableHead>
            <TableHead>Guadagnato</TableHead>
            <TableHead>Da incassare</TableHead>
            <TableHead>Già pagato</TableHead>
            <TableHead className="text-right">Azione</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Caricamento…</TableCell></TableRow>}
            {!loading && data.rows.map((r) => (
              <TableRow key={r.referrer_id} data-testid={`payout-row-${r.referrer_id}`}>
                <TableCell className="font-medium">
                  <EntityLink id={r.referrer_id} data-testid={`payout-account-link-${r.referrer_id}`}>{r.company_name || r.name || r.email || r.referrer_id}</EntityLink>
                  {r.role && <Badge variant="outline" className="ml-2 uppercase text-[10px]">{r.role === "customer" ? "privato" : "business"}</Badge>}
                </TableCell>
                <TableCell>{r.iscritti_attivi}</TableCell>
                <TableCell>{euro(r.guadagnato)}</TableCell>
                <TableCell className="text-amber-500 font-semibold" data-testid={`payout-pending-${r.referrer_id}`}>{euro(r.da_incassare)}</TableCell>
                <TableCell className="text-teal-500">{euro(r.gia_pagato)}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" disabled={r.da_incassare <= 0} onClick={() => setTarget(r)} data-testid={`payout-settle-btn-${r.referrer_id}`}>
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Liquida
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!loading && data.rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Nessuna provvigione referral registrata.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <AlertDialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <AlertDialogContent data-testid="payout-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confermi la liquidazione?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per segnare come <strong>già pagato</strong> l'importo di <strong>{euro(target?.da_incassare)}</strong> per <strong>{target?.company_name || target?.name}</strong>.
              L'importo NON verrà accreditato sul wallet (pagamento gestito fuori piattaforma). Verrà inviata un'email di notifica al dealer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="payout-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); settle(); }} disabled={settling} data-testid="payout-confirm">
              {settling ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Attendi…</> : "Conferma liquidazione"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
